import { useEffect, useId, useMemo, useState, type FormEvent } from 'react';
import {
  AXES,
  LIBELLES_AXES,
  POIDS_DEFAUT,
  dateHeureTunis,
  normaliserA100,
  repartirPoids,
  sommePoids,
  totalA100,
  type Axe,
  type BaremeFamille,
  type Poids,
} from '@rh/shared';
import { appel } from '../api';
import { Chargement, Erreur } from '../composants/Etat';
import { NavAdministration } from '../composants/NavAdministration';

const ZERO = Object.fromEntries(AXES.map((axe) => [axe, 0])) as Poids;
const pourcent = (valeur: number) => `${valeur.toLocaleString('fr-FR', { maximumFractionDigits: 1 })}\u202f%`;
const saisiesDe = (poids: Poids) => Object.fromEntries(AXES.map((axe) => [axe, String(poids[axe])])) as Record<Axe, string>;

function poidsDeDepart(famille: BaremeFamille): Poids {
  if (famille.enVigueur) return famille.enVigueur.poids;
  return famille.groupe ? POIDS_DEFAUT[famille.groupe] : ZERO;
}

/** Poids des barèmes par famille, versionnés (docs/BAREME.md § Poids par défaut). */
export function AdminBaremes() {
  const [familles, setFamilles] = useState<BaremeFamille[] | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [choix, setChoix] = useState('');
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    appel<BaremeFamille[]>('/baremes')
      .then((liste) => {
        setFamilles(liste);
        setChoix((actuel) => actuel || (liste.find((f) => f.actif) ?? liste[0])?.familleId || '');
      })
      .catch((e: Error) => setErreur(e.message));
  }, []);

  const famille = familles?.find((f) => f.familleId === choix) ?? null;

  return (
    <section>
      <NavAdministration />
      <div className="entete-annonce">
        <h2>Barèmes</h2>
      </div>
      <p className="mention">
        Poids des neuf axes de la note d’entretien, par métier. Chaque enregistrement crée une version datée : les
        annonces déjà validées gardent la version qu’elles ont figée, les entretiens déjà notés aussi.
      </p>
      {erreur && <Erreur message={erreur} />}
      {!familles && !erreur && <Chargement quoi="des barèmes" />}
      {familles && (
        <div className="champ bloc">
          <label htmlFor="famille-bareme">Métier</label>
          <select id="famille-bareme" value={choix} onChange={(e) => {
              setChoix(e.target.value);
              setMessage(null);
            }}>
            {familles.map((f) => (
              <option key={f.familleId} value={f.familleId}>
                {f.libelle}
                {f.enVigueur ? ` — version ${f.enVigueur.version}, ${pourcent(f.enVigueur.total)}` : ' — aucune version'}
                {f.actif ? '' : ' (désactivé)'}
              </option>
            ))}
          </select>
        </div>
      )}
      {message && (
        <p className="notice-cv" role="status">
          {message}
        </p>
      )}
      {famille && (
        <EditeurBareme
          key={`${famille.familleId}-${famille.enVigueur?.id ?? 'aucun'}`}
          famille={famille}
          onEnregistre={(maj, texte) => {
            setFamilles((liste) => (liste ?? []).map((f) => (f.familleId === maj.familleId ? maj : f)));
            setMessage(texte);
          }}
        />
      )}
    </section>
  );
}

function EditeurBareme({
  famille,
  onEnregistre,
}: {
  famille: BaremeFamille;
  onEnregistre: (famille: BaremeFamille, message: string) => void;
}) {
  const id = useId();
  const depart = useMemo(() => poidsDeDepart(famille), [famille]);
  const [poids, setPoids] = useState<Poids>(depart);
  const [saisies, setSaisies] = useState<Record<Axe, string>>(() => saisiesDe(depart));
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  /** Axes déjà saisis : ils ne bougent plus quand un autre poids change. */
  const [touches, setTouches] = useState<Axe[]>([]);

  const total = Math.round(sommePoids(poids) * 10) / 10;
  const modifie = AXES.some((axe) => poids[axe] !== depart[axe]);
  const enVigueur = famille.enVigueur;

  function appliquer(nouveaux: Poids): void {
    setPoids(nouveaux);
    setSaisies(saisiesDe(nouveaux));
    setTouches([]);
    setErreur(null);
  }

  /** À la sortie du champ : le poids saisi est posé, les axes pas encore modifiés se réajustent vers 100 %. */
  function valider(axe: Axe): void {
    const lu = Number(saisies[axe].replace(',', '.'));
    if (saisies[axe].trim() === '' || !Number.isFinite(lu)) {
      setSaisies(saisiesDe(poids));
      return;
    }
    const valeur = Math.round(Math.min(100, Math.max(0, lu)) * 10) / 10;
    if (valeur === poids[axe]) {
      setSaisies(saisiesDe(poids));
      return;
    }
    const figes = touches.filter((a) => a !== axe);
    const nouveaux = repartirPoids(poids, axe, valeur, figes);
    setPoids(nouveaux);
    setSaisies(saisiesDe(nouveaux));
    setTouches([...figes, axe]);
    setErreur(null);
  }

  async function enregistrer(evenement: FormEvent): Promise<void> {
    evenement.preventDefault();
    const version = (famille.versions[0]?.version ?? 0) + 1;
    if (
      !window.confirm(
        `Enregistrer la version ${version} du barème « ${famille.libelle} » ?\n\n` +
          'Les annonces déjà validées et les entretiens déjà notés gardent leur version. ' +
          'Les annonces pas encore validées utiliseront celle-ci ; une note d’entretien en attente d’envoi sur un téléphone sera à revoir.',
      )
    ) {
      return;
    }
    setEnvoi(true);
    setErreur(null);
    try {
      const maj = await appel<BaremeFamille>(`/baremes/${famille.familleId}`, {
        method: 'PUT',
        body: JSON.stringify({ poids, depuisVersion: enVigueur?.version ?? null }),
      });
      // La page garde le message : l'éditeur est remonté sur la nouvelle version.
      onEnregistre(maj, `Version ${maj.enVigueur?.version ?? version} enregistrée.`);
    } catch (e) {
      setErreur(e instanceof Error ? e.message : 'Enregistrement impossible');
    } finally {
      setEnvoi(false);
    }
  }

  return (
    <>
      <section className="bloc">
        <h3>
          {famille.libelle} : {enVigueur ? `version ${enVigueur.version} en vigueur` : 'aucune version'}
        </h3>
        {enVigueur && (
          <p className="mention">
            {enVigueur.creePar ? `Créée le ${dateHeureTunis(enVigueur.creeLe)} par ${enVigueur.creePar}` : 'Version initiale du semis'}
            {' · '}
            {enVigueur.annoncesFigees} annonce(s) validée(s) l’ont figée · {enVigueur.evaluations} entretien(s) noté(s)
          </p>
        )}
        {enVigueur && !totalA100(enVigueur.poids) && (
          <p className="signal">
            La version en vigueur totalise {pourcent(enVigueur.total)} : la note est divisée par la somme réelle des
            poids. Une nouvelle version totalisera 100 %.
          </p>
        )}
        <form noValidate onSubmit={(e) => void enregistrer(e)}>
          <table className="table-poids">
            <thead>
              <tr>
                <th scope="col">Axe</th>
                <th scope="col">Poids (%)</th>
              </tr>
            </thead>
            <tbody>
              {AXES.map((axe) => (
                <tr key={axe}>
                  <th scope="row">
                    <label htmlFor={`${id}-${axe}`}>{LIBELLES_AXES[axe]}</label>
                  </th>
                  <td>
                    <input
                      id={`${id}-${axe}`}
                      type="number"
                      inputMode="decimal"
                      min={0}
                      max={100}
                      step={0.1}
                      value={saisies[axe]}
                      onChange={(e) => setSaisies((s) => ({ ...s, [axe]: e.target.value }))}
                      onBlur={() => valider(axe)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          valider(axe);
                        }
                      }}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <th scope="row">Total</th>
                <td className={totalA100(poids) ? undefined : 'total-faux'} aria-live="polite">
                  {pourcent(total)}
                </td>
              </tr>
            </tfoot>
          </table>
          <p className="mention">
            En quittant un champ, les axes pas encore modifiés se réajustent en proportion pour viser 100 % ; les
            poids déjà saisis ne bougent plus.
          </p>
          <div className="formulaire-admin">
            <div className="boutons">
              {!totalA100(poids) && (
                <button type="button" className="bouton-second" onClick={() => appliquer(normaliserA100(poids))}>
                  Ramener à 100 % (proportions gardées)
                </button>
              )}
              {modifie && (
                <button type="button" className="bouton-second" onClick={() => appliquer(depart)}>
                  Annuler les modifications
                </button>
              )}
              <button
                type="submit"
                className="bouton"
                disabled={envoi || !totalA100(poids) || (!modifie && Boolean(enVigueur))}
              >
                Enregistrer une nouvelle version
              </button>
            </div>
          </div>
        </form>
        {erreur && <Erreur message={erreur} />}
      </section>

      {famille.versions.length > 0 && (
        <section className="bloc">
          <h3>Historique</h3>
          <div className="defilement">
            <table className="table-stats historique-baremes">
              <thead>
                <tr>
                  <th scope="col">Version</th>
                  <th scope="col">Créée le</th>
                  <th scope="col">Par</th>
                  <th scope="col">Total</th>
                  <th scope="col">Annonces figées</th>
                  <th scope="col">Entretiens</th>
                </tr>
              </thead>
              <tbody>
                {famille.versions.map((v) => (
                  <tr key={v.id}>
                    <th scope="row">
                      {v.version}
                      {v.id === enVigueur?.id ? ' (en vigueur)' : ''}
                    </th>
                    <td>{dateHeureTunis(v.creeLe)}</td>
                    <td>{v.creePar ?? 'Semis'}</td>
                    <td>{pourcent(v.total)}</td>
                    <td>{v.annoncesFigees}</td>
                    <td>{v.evaluations}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </>
  );
}
